import { css } from 'styled-components';

/**
 * Dark-mode overrides for Ant Design 3.x base components that have no other
 * dark-aware override. Antd ships light CSS (white surfaces, near-black text,
 * light borders); without these, Tables/Tabs/Inputs/Cards/Collapses render
 * light on the dark theme. Everything here is GATED on `theme.name === 'dark'`,
 * so light mode keeps antd's default look untouched (zero light-mode risk).
 */
const darkMode = css`
  ${({ theme }) =>
    theme.name === 'dark' &&
    css`
      /* ---------- Typography / generic text ---------- */
      .ant-typography,
      .ant-form,
      .ant-form-item,
      .ant-form label,
      .ant-list-item,
      .ant-list-item-meta-title,
      .ant-descriptions-item-content,
      .ant-descriptions-item-label {
        color: ${theme.colors.element.primary};
      }
      .ant-list-item-meta-description,
      .ant-list-empty-text {
        color: ${theme.colors.element.secondary};
      }

      /* ---------- Card ---------- */
      .ant-card {
        background-color: ${theme.colors.background.secondary};
        color: ${theme.colors.element.primary};
        border-color: ${theme.colors.divider.primary};
      }
      .ant-card-head {
        color: ${theme.colors.element.primary};
        border-bottom-color: ${theme.colors.divider.primary};
      }
      .ant-card-bordered {
        border-color: ${theme.colors.divider.primary};
      }
      .ant-card-actions {
        background: ${theme.colors.background.secondary};
        border-top-color: ${theme.colors.divider.primary};
        > li {
          border-right-color: ${theme.colors.divider.primary};
        }
      }

      /* ---------- Table ---------- */
      .ant-table {
        color: ${theme.colors.element.primary};
        background: ${theme.colors.background.secondary};
      }
      .ant-table-thead > tr > th {
        background: ${theme.colors.background.tertiary};
        color: ${theme.colors.element.secondary};
        border-bottom-color: ${theme.colors.divider.primary};
      }
      .ant-table-tbody > tr > td {
        background: ${theme.colors.background.secondary};
        border-bottom-color: ${theme.colors.divider.primary};
      }
      .ant-table-tbody > tr.ant-table-row:hover > td,
      .ant-table-tbody > tr > td.ant-table-cell-row-hover {
        background: ${theme.colors.state.hover};
      }
      .ant-table-placeholder {
        background: ${theme.colors.background.secondary};
        border-bottom-color: ${theme.colors.divider.primary};
        color: ${theme.colors.element.secondary};
      }
      .ant-table-footer,
      .ant-table-title {
        background: ${theme.colors.background.tertiary};
        color: ${theme.colors.element.secondary};
      }
      .ant-table-tbody > tr > td,
      .ant-table-small {
        border-color: ${theme.colors.divider.primary};
      }

      /* ---------- Tabs ---------- */
      .ant-tabs {
        color: ${theme.colors.element.primary};
      }
      .ant-tabs-bar,
      .ant-tabs-nav-container {
        border-bottom-color: ${theme.colors.divider.primary};
      }
      .ant-tabs-tab {
        color: ${theme.colors.element.secondary};
      }
      .ant-tabs-tab:hover {
        color: ${theme.colors.brand.primary};
      }
      .ant-tabs-tab-active {
        color: ${theme.colors.brand.primary};
      }
      .ant-tabs-ink-bar {
        background-color: ${theme.colors.brand.primary};
      }
      .ant-tabs-bar {
        border-color: ${theme.colors.divider.primary};
      }

      /* ---------- Collapse ---------- */
      .ant-collapse,
      .ant-collapse-content {
        background: ${theme.colors.background.secondary};
        color: ${theme.colors.element.primary};
        border-color: ${theme.colors.divider.primary};
      }
      .ant-collapse > .ant-collapse-item {
        border-bottom-color: ${theme.colors.divider.primary};
      }
      .ant-collapse > .ant-collapse-item > .ant-collapse-header {
        color: ${theme.colors.element.primary};
      }
      .ant-collapse-content {
        border-top-color: ${theme.colors.divider.primary};
      }

      /* ---------- Inputs / TextArea / InputNumber ---------- */
      .ant-input,
      .ant-input-number,
      .ant-input-affix-wrapper,
      .ant-input-number-input,
      textarea.ant-input {
        background-color: ${theme.colors.background.primary};
        color: ${theme.colors.element.primary};
        border-color: ${theme.colors.divider.primary};
      }
      .ant-input::placeholder,
      .ant-input-number-input::placeholder,
      textarea.ant-input::placeholder {
        color: ${theme.colors.element.placeholder};
      }
      .ant-input:hover,
      .ant-input-affix-wrapper:hover,
      .ant-input-number:hover {
        border-color: ${theme.colors.brand.primary};
      }
      .ant-input:focus,
      .ant-input-affix-wrapper-focused,
      .ant-input-number-focused {
        border-color: ${theme.colors.brand.primary};
        box-shadow: 0 0 0 2px ${theme.colors.brand.secondary};
      }
      .ant-input-group-addon {
        background-color: ${theme.colors.background.tertiary};
        color: ${theme.colors.element.secondary};
        border-color: ${theme.colors.divider.primary};
      }
      .ant-input[disabled],
      .ant-input-number-disabled {
        background-color: ${theme.colors.background.tertiary};
        color: ${theme.colors.element.disabled};
      }

      /* ---------- Select (base, antd) ---------- */
      .ant-select {
        color: ${theme.colors.element.primary};
      }
      .ant-select-selection,
      .ant-select-selector {
        background-color: ${theme.colors.background.primary};
        border-color: ${theme.colors.divider.primary};
        color: ${theme.colors.element.primary};
      }
      /* Disabled select keeps antd's light grey surface otherwise (e.g. "Motivo"
         before a "Tipo" is picked). Match the disabled-input treatment. */
      .ant-select-disabled .ant-select-selection,
      .ant-select-disabled .ant-select-selection:hover {
        background-color: ${theme.colors.background.tertiary};
        color: ${theme.colors.element.disabled};
      }
      .ant-select-dropdown {
        background-color: ${theme.colors.background.primary};
        color: ${theme.colors.element.primary};
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.45);
      }
      .ant-select-dropdown-menu-item {
        color: ${theme.colors.element.primary};
      }
      /* :not(...-disabled) matches antd v3's own specificity so these win over its
         light/pink active+selected backgrounds (antd themes them with the primary tint). */
      .ant-select-dropdown-menu-item-active:not(.ant-select-dropdown-menu-item-disabled),
      .ant-select-dropdown-menu-item:hover:not(.ant-select-dropdown-menu-item-disabled) {
        background-color: ${theme.colors.state.hover};
        color: ${theme.colors.element.primary};
      }
      .ant-select-dropdown-menu-item-selected,
      .ant-select-dropdown-menu-item-selected:not(.ant-select-dropdown-menu-item-disabled) {
        background-color: ${theme.colors.brand.secondary};
        color: ${theme.colors.element.primary};
      }
      .ant-select-arrow,
      .ant-select-selection__placeholder {
        color: ${theme.colors.element.placeholder};
      }

      /* ---------- Radio / Checkbox ---------- */
      .ant-radio-wrapper,
      .ant-checkbox-wrapper {
        color: ${theme.colors.element.primary};
      }
      .ant-radio-inner,
      .ant-checkbox-inner {
        background-color: ${theme.colors.background.primary};
        border-color: ${theme.colors.element.disabled};
      }
      .ant-radio-checked .ant-radio-inner,
      .ant-checkbox-checked .ant-checkbox-inner {
        border-color: ${theme.colors.brand.primary};
      }
      .ant-checkbox-checked .ant-checkbox-inner {
        background-color: ${theme.colors.brand.primary};
      }
      .ant-radio-inner::after {
        background-color: ${theme.colors.brand.primary};
      }

      /* ---------- Pagination ---------- */
      .ant-pagination-item,
      .ant-pagination-prev .ant-pagination-item-link,
      .ant-pagination-next .ant-pagination-item-link {
        background-color: ${theme.colors.background.primary};
        border-color: ${theme.colors.divider.primary};
        color: ${theme.colors.element.primary};
      }
      .ant-pagination-item a {
        color: ${theme.colors.element.primary};
      }
      .ant-pagination-item-active {
        border-color: ${theme.colors.brand.primary};
        a {
          color: ${theme.colors.brand.primary};
        }
      }

      /* ---------- Dropdown / Menu ---------- */
      .ant-dropdown-menu,
      .ant-menu {
        background-color: ${theme.colors.background.primary};
        color: ${theme.colors.element.primary};
      }
      .ant-dropdown-menu-item,
      .ant-menu-item {
        color: ${theme.colors.element.primary};
        &:hover {
          background-color: ${theme.colors.state.hover};
        }
      }

      /* ---------- Empty / Spin / Divider / Badge ---------- */
      .ant-empty-description {
        color: ${theme.colors.element.secondary};
      }
      .ant-divider {
        border-color: ${theme.colors.divider.primary};
        color: ${theme.colors.element.primary};
      }
      .ant-divider-horizontal.ant-divider-with-text-center::before,
      .ant-divider-horizontal.ant-divider-with-text-center::after {
        border-top-color: ${theme.colors.divider.primary};
      }
      .ant-spin-text {
        color: ${theme.colors.brand.primary};
      }

      /* ---------- Buttons ---------- */
      /* Default antd buttons render white on light CSS; give them a dark surface. */
      .ant-btn {
        background-color: ${theme.colors.background.primary};
        border-color: ${theme.colors.divider.primary};
        color: ${theme.colors.element.primary};
        &:hover,
        &:focus {
          background-color: ${theme.colors.background.tertiary};
          border-color: ${theme.colors.brand.primary};
          color: ${theme.colors.brand.primary};
        }
      }
      /* Primary / search-action buttons use the backoffice pink. */
      .ant-btn-primary {
        background-color: ${theme.colors.brand.primary};
        border-color: ${theme.colors.brand.primary};
        color: ${theme.colors.brand.overPrimary};
        &:hover,
        &:focus {
          background-color: ${theme.colors.brand.primaryAlt};
          border-color: ${theme.colors.brand.primaryAlt};
          color: ${theme.colors.brand.overPrimary};
        }
      }
      .ant-btn-link {
        background-color: transparent;
        border-color: transparent;
        color: ${theme.colors.brand.primary};
      }
      .ant-btn[disabled],
      .ant-btn[disabled]:hover {
        background-color: ${theme.colors.background.tertiary};
        border-color: ${theme.colors.divider.primary};
        color: ${theme.colors.element.disabled};
      }

      /* ---------- Tag (antd default) ---------- */
      .ant-tag {
        background: ${theme.colors.background.tertiary};
        border-color: ${theme.colors.divider.primary};
        color: ${theme.colors.element.primary};
      }

      /* ---------- Calendar / Badge cell ---------- */
      .ant-fullcalendar,
      .ant-calendar {
        background: ${theme.colors.background.secondary};
        color: ${theme.colors.element.primary};
      }

      /* ---------- Modal (plain antd modals without the .mt-modal class) ---------- */
      .ant-modal-content {
        background-color: ${theme.colors.background.secondary};
        color: ${theme.colors.element.primary};
      }
      .ant-modal-header {
        background-color: ${theme.colors.background.secondary};
        border-bottom-color: ${theme.colors.divider.primary};
      }
      .ant-modal-title {
        color: ${theme.colors.element.primary};
      }
      .ant-modal-footer {
        border-top-color: ${theme.colors.divider.primary};
      }
      .ant-modal-close {
        color: ${theme.colors.element.secondary};
      }

      /* ---------- Popover (plain antd popovers without an explicit dark bg) ---------- */
      /* Popovers that only use the mt-popover-* helper classes (no-arrow / no-padding /
         border-radius) never set a surface, so they fall back to antd's white inner.
         Single-class selectors here lose to .mt-custom-tooltip / .mt-custom-popover
         (two-class), so those keep their own backgrounds. */
      .ant-popover-inner {
        background-color: ${theme.colors.background.secondary};
      }
      .ant-popover-inner-content {
        color: ${theme.colors.element.primary};
      }
      .ant-popover-arrow {
        border-color: ${theme.colors.background.secondary};
        background-color: ${theme.colors.background.secondary};
      }

      /* ---------- Notification (e.g. session-expiry alert) ---------- */
      .ant-notification-notice {
        background: ${theme.colors.background.secondary};
        color: ${theme.colors.element.primary};
        box-shadow: 0 3px 6px -4px rgba(0, 0, 0, 0.6), 0 6px 16px 0 rgba(0, 0, 0, 0.5);
      }
      .ant-notification-notice-message,
      .ant-notification-notice-description,
      .ant-notification-notice-close {
        color: ${theme.colors.element.primary};
      }

      /* ---------- Message (toasts) ---------- */
      .ant-message-notice-content {
        background: ${theme.colors.background.secondary};
        color: ${theme.colors.element.primary};
        box-shadow: 0 3px 6px -4px rgba(0, 0, 0, 0.6);
      }

      /* ---------- Timeline (e.g. Histórico tab) ---------- */
      .ant-timeline {
        color: ${theme.colors.element.primary};
      }
      .ant-timeline-item-content,
      .ant-timeline-item-content b,
      .ant-timeline-item-content i {
        color: ${theme.colors.element.primary};
      }
      .ant-timeline-item-tail {
        border-left-color: ${theme.colors.divider.primary};
      }
      .ant-timeline-item-head {
        background-color: ${theme.colors.background.secondary};
      }

      /* ---------- Form labels / help / Modal confirm ---------- */
      .ant-form-item-label > label,
      .ant-form-item-required {
        color: ${theme.colors.element.primary};
      }
      .ant-form-explain,
      .ant-form-extra {
        color: ${theme.colors.element.secondary};
      }
      .ant-modal-confirm-title,
      .ant-modal-confirm-content {
        color: ${theme.colors.element.primary};
      }

      /* ---------- Steps ---------- */
      .ant-steps-item-title,
      .ant-steps-item-description {
        color: ${theme.colors.element.primary};
      }
      .ant-steps-item-wait .ant-steps-item-title,
      .ant-steps-item-wait .ant-steps-item-description {
        color: ${theme.colors.element.placeholder};
      }
    `}
`;

export default darkMode;
